import { FC, JSX, useContext, useEffect, useState } from 'react';

import { Footer, Header } from '@displate/styleguide';

import { useVwoTest } from '@/common/abtest/useVwoTest';
import { initializeVWOAsynchronous } from '@/common/abtest/vwoInitialize';
import { trackAutocompletePhraseClick } from '@/common/analytics/athena/trackAutocompletePhraseClick';
import NextPageProgressBar from '@/common/components/NextPageProgressBar/NextPageProgressBar';
import { StaticImage } from '@/common/constants/displateRoutes';
import { partners } from '@/common/constants/partners';
import { MAX_CHARACTERS, MIN_CHARACTERS } from '@/common/constants/search';
import { socials } from '@/common/constants/socials';
import { PromoBarContext } from '@/common/contexts/PromoBarContext/PromoBarContext';
import { useAthena } from '@/common/hooks/useAthena/useAthena';
import {
  useMostPopularSearches,
  useSuggestions,
} from '@/common/hooks/useAutocomplete/useAutocomplete';
import { useClientStack } from '@/common/hooks/useClientStack/useClientStack';
import { useHeaderContentInfo } from '@/common/hooks/useHeaderContent/useHeaderContentInfo';
import { IconSize } from '@/common/icons/icon.interface';
import { mapCartItems } from '@/common/mappers/CartItemMapper';
import { mapTopBar } from '@/common/mappers/PromoMapper';
import { mapSuggestions } from '@/common/mappers/SuggestionMapper';
import { useCurrentPage } from '@/common/routes/ElysiumKnownPages';
import { AuthContext } from '@/contexts/auth/AuthContext';
import { useBrowseCollectionsLinksViaRouter } from '@/modules/MainLayout/hooks/useBrowseCollectionsLinksViaRouter';
import {
  DEFAULT_HEADER_HEIGHTS,
  DefaultHeaderHeightsProps,
  useHeaderAndFooterHeight,
} from '@/modules/MainLayout/hooks/useHeaderAndFooterHeight';
import { MainContainer } from '@/modules/MainLayout/MainLayout.styled';
import { TopBar } from 'generatedBfw/static';

import { useBrowseBrandsLinksViaRouter } from './hooks/useBrowseBrandsLinksViaRouter';

interface MainLayoutProps {
  children: JSX.Element;
}

export interface MainLayoutStaticProps {
  topBarData: TopBar;
  isMobileOrTablet: boolean;
  showFooter?: boolean;
  shouldShowSearchBarOnTopOfPage?: boolean;
  isTopbarVisible?: boolean;
  useH1?: boolean;
  defaultHeaderHeights?: DefaultHeaderHeightsProps;
}

export const MainLayout: FC<MainLayoutProps & MainLayoutStaticProps> = ({
  children,
  topBarData,
  isMobileOrTablet,
  useH1 = false,
  showFooter = true,
  shouldShowSearchBarOnTopOfPage = true,
  isTopbarVisible = true,
  defaultHeaderHeights = DEFAULT_HEADER_HEIGHTS,
}): JSX.Element => {
  const { userInfo, login, logout } = useContext(AuthContext);
  const {
    isPromoBarOpen,
    isPromoBarFloating,
    isPromoBarScrolled,
    promoBarHeight,
    setLayoutConfiguration,
  } = useContext(PromoBarContext);

  const [navigationBarHeight, setNavigationBarHeight] = useState(0);
  const [searchBarHeight, setSearchBarHeight] = useState(0);
  const [searchPhrase, setSearchPhrase] = useState('');
  const conr2659 = useVwoTest('conr2659');

  const { addClientStackItem: addSessionStackItem } = useClientStack();
  const { data: headerContent, isFetching: isFetchingHeaderContent } = useHeaderContentInfo();
  const { heights, refs } = useHeaderAndFooterHeight({
    isMobileOrTablet,
    defaultHeaderHeights: defaultHeaderHeights,
  });
  const { data: mostPopularSearches } = useMostPopularSearches();
  const { data: suggestions, isError: isSuggestionsError } = useSuggestions(searchPhrase ?? '', {
    enabled: searchPhrase?.length
      ? searchPhrase?.length > MIN_CHARACTERS && searchPhrase?.length <= MAX_CHARACTERS
      : false,
    refetchOnMount: false,
  });
  const pageConfig = useCurrentPage();

  const handleUserLogout = () => {
    addSessionStackItem({
      event: 'trackUserLogout',
    });
    logout();
  };

  useBrowseCollectionsLinksViaRouter();
  useBrowseBrandsLinksViaRouter();

  useEffect(() => {
    // this variable is workaround for ipads which are recognized as desktop devices
    // but styleguide header is working on mobile version
    // 2px addition is coming from headers style box-shadow: 2px
    const conditionalSearchBarHeight = searchBarHeight < 60 ? searchBarHeight + 2 : 0;
    setLayoutConfiguration(
      heights.header - (shouldShowSearchBarOnTopOfPage ? conditionalSearchBarHeight : 0),
      heights.header + navigationBarHeight,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMobileOrTablet,
    heights,
    searchBarHeight,
    navigationBarHeight,
    shouldShowSearchBarOnTopOfPage,
  ]);

  const { pushEvent } = useAthena();

  return (
    <>
      {initializeVWOAsynchronous()}
      <NextPageProgressBar />
      <Header
        abTest={{ id: 'conr2659', variant: conr2659.variant || 'control' }}
        accountRoles={userInfo?.accountRoles ?? []}
        cartCount={headerContent?.cartPreview.itemCount ?? 0}
        cartPreviewItems={mapCartItems(headerContent?.cartPreview.items)}
        debouncedSearchTerm={searchPhrase}
        isAutocompleteError={isSuggestionsError}
        isFetching={isFetchingHeaderContent}
        isMobileOrTablet={isMobileOrTablet}
        isTopbarVisible={isTopbarVisible}
        isUserLoggedIn={userInfo?.isUserLoggedIn ?? false}
        mostPopularSearches={mostPopularSearches?.phrases || []}
        onAutocompleHintsClick={(params) =>
          trackAutocompletePhraseClick({ ...params, pageConfig }, pushEvent)
        }
        onLogin={login}
        onLogout={handleUserLogout}
        onSearchPhraseSelected={(redirectUrl: string) => (window.location.href = redirectUrl)}
        ref={refs.headerRef}
        setNavigationBarHeight={setNavigationBarHeight}
        setSearchBarHeight={setSearchBarHeight}
        setSearchPhrase={setSearchPhrase}
        shouldShowSearchBarOnTopOfPage={shouldShowSearchBarOnTopOfPage}
        suggestions={mapSuggestions(suggestions)}
        topbarData={mapTopBar(topBarData)}
        useH1={useH1}
        userAvatar={userInfo?.avatar ?? undefined}
        userNick={userInfo?.nick ?? undefined}
        wishlistCount={headerContent?.wishlistItemsCount ?? 0}
      />
      <MainContainer
        data-testid="main-container"
        footerHeight={heights.footer}
        headerHeight={heights.header}
        navigationBarHeight={navigationBarHeight}
        promoBarHeight={
          isPromoBarOpen && isPromoBarScrolled && !isPromoBarFloating ? promoBarHeight || 0 : 0
        }
      >
        {children}
      </MainContainer>
      {showFooter && (
        <Footer
          accountRoles={userInfo?.accountRoles ?? []}
          partners={partners}
          ref={refs.footerRef}
          securedPaymentsLogo={StaticImage('/images/footer/footer_payment_processors.svg')}
          socials={socials.map((social) => ({
            url: social.url,
            icon: <social.icon height={IconSize.SIZE30} width={IconSize.SIZE30} />,
          }))}
        />
      )}
      <div id="pdp-padding-bottom" />
    </>
  );
};
